// 005 slice / 007 (P-HOME-1, P-HOME-2): the parent Home hero. "Shopping for" chips list the linked
// children; the selected child's school's live banners show in the hero carousel (fallback: the
// school's name). Choosing a child at another school switches the banners, and the header follows
// (004 FR-026). Categories and booklists come with the rest of 005.
import { useEffect, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";

import { selectStudent, useSelectedStudent } from "@/profile/selection";
import { useParentData } from "@/profile/useParentData";
import { standardClassName } from "@/profile/viewModels";
import { makeStyles } from "@/theme/makeStyles";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { type HeroBanner, HeroCarousel } from "./HeroCarousel";
import { useHomeClient } from "./homeApi";

export const HOME_COPY = {
  shoppingFor: "Shopping for",
  heroFallback: "Books & stationery for the new session",
  chip: (name: string, classLevel: string, section: string) =>
    `${name} · ${standardClassName(classLevel)} ${section}`,
} as const;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    scroll: { flex: 1 },
    page: {
      width: "100%",
      maxWidth: 1080,
      alignSelf: "center",
      padding: t.space.lg,
      gap: t.space.lg,
    },
    selector: { gap: t.space.sm },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    chip: {
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.pill,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
      justifyContent: "center",
    },
    chipOn: {
      borderColor: t.color.primary,
      backgroundColor: t.color.primarySoft,
    },
  }),
);

export function HomeScreen() {
  const styles = useStyles();
  const client = useHomeClient();
  const { students } = useParentData();
  const selectedId = useSelectedStudent();
  const linked = (students.data ?? []).filter(
    (s) => s.linked && s.school !== null,
  );
  const selected = linked.find((s) => s.id === selectedId) ?? linked[0] ?? null;
  const schoolId = selected?.school?.id ?? null;
  const [banners, setBanners] = useState<{
    schoolId: string;
    items: HeroBanner[];
  } | null>(null);

  useEffect(() => {
    if (!schoolId) return;
    let live = true;
    client.listLiveSchoolBanners(schoolId).then(
      (list) => {
        if (live) setBanners({ schoolId, items: list.items });
      },
      () => {
        // The hero falls back to the school's name; Home stays usable (P-HOME-1).
        if (live) setBanners({ schoolId, items: [] });
      },
    );
    return () => {
      live = false;
    };
  }, [client, schoolId]);

  const items = banners && banners.schoolId === schoolId ? banners.items : [];

  return (
    <ScrollView style={styles.scroll} testID="home-screen">
      <View style={styles.page} role="main">
        {linked.length > 0 ? (
          <View style={styles.selector}>
            <Text weight="semiBold" nativeID="shopping-for">
              {HOME_COPY.shoppingFor}
            </Text>
            <View
              style={styles.chips}
              role="radiogroup"
              aria-labelledby="shopping-for"
            >
              {linked.map((s) => {
                const label = HOME_COPY.chip(
                  s.name,
                  s.classLevel ?? "",
                  s.section ?? "",
                );
                const on = s.id === selected?.id;
                return (
                  <PressableTarget
                    key={s.id}
                    accessibilityRole="radio"
                    accessibilityLabel={label}
                    accessibilityState={{ checked: on }}
                    aria-checked={on}
                    onPress={() => selectStudent(s.id)}
                    style={on ? [styles.chip, styles.chipOn] : styles.chip}
                  >
                    <Text weight={on ? "semiBold" : "regular"}>{label}</Text>
                  </PressableTarget>
                );
              })}
            </View>
          </View>
        ) : null}
        {selected?.school ? (
          <HeroCarousel
            banners={items}
            schoolName={selected.school.name}
            fallbackText={HOME_COPY.heroFallback}
          />
        ) : null}
      </View>
    </ScrollView>
  );
}
